import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Calendar,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Download,
  ExternalLink,
  Flag,
  Handshake,
  Hourglass,
  Loader2,
  MapPin,
  Pencil,
  Phone,
  Plus,
  StickyNote,
  Trash2,
  User,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { formatRelativeDate, initials } from "@/lib/formatters";
import {
  downloadIcs,
  googleCalendarUrl,
  outlookCalendarUrl,
} from "@/lib/calendarLinks";
import {
  formatDateTimeInTz,
  formatTimeInTz,
  formatTimezoneLabel,
  getDefaultTimezone,
  splitDateTimeInTz,
  toNaiveIsoDateTime,
} from "@/lib/timezones";
import { TimezoneSelect } from "@/components/shared/TimezoneSelect";
import { Globe } from "lucide-react";
import {
  getStatusColor,
  scoreColor,
} from "@/lib/constants";
import { usePermissions } from "@/hooks/usePermissions";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useAuthStore } from "@/stores/auth.store";
import { useAttachments } from "@/hooks/useAttachments";
import { useDebounce } from "@/hooks/useDebounce";
import { AttachmentZone } from "@/components/attachments/AttachmentZone";
import { invalidateAttachmentsCellCache } from "./AttachmentsCell";
import {
  completeTask,
  deleteTask,
  reopenTask,
  updateTask,
} from "@/api/tasks";
import { listMembers } from "@/api/projects";
import { listCompanies, getCompany, updateCompany } from "@/api/companies";
import { logAction } from "@/api/activity";
import { fromTaskState } from "@/lib/taskReturn";
import {
  COPIED_FROM_COMPANY_LABEL,
  formatPhone,
  phoneCopiedFromCompany,
  phoneForCopy,
  phoneHref,
} from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import {
  PRIORITIES,
  PRIORITY_DOT,
  PRIORITY_LABEL,
  PRIORITY_RING,
} from "./priority";
import {
  combineDateTime,
  splitDateTime,
  timeSlots,
} from "./taskFormatters";
import { getEffectivePipelineStatuses } from "@/lib/projectConfig";
import type {
  AttachmentRead,
  Company,
  CompanyDetail,
  EmbeddedContact,
  PipelineStatus,
  ProjectMember,
  TaskRead,
  TaskUpdate,
} from "@/api/types";

const NO_TIME = "__notime__";
const ACTIVITY_LIMIT = 3;

interface TaskDetailSheetProps {
  task: TaskRead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  /** Bubbled up so parents can patch their cached lists. */
  onTaskUpdated: (task: TaskRead) => void;
  /** Fired after the task is deleted; parent removes from list and we close. */
  onTaskDeleted: (id: number) => void;
  /** When clicked, the parent should open TaskCreateDialog in edit mode. */
  onAskEdit?: (task: TaskRead) => void;
}

export function TaskDetailSheet({
  task,
  open,
  onOpenChange,
  projectSlug,
  onTaskUpdated,
  onTaskDeleted,
  onAskEdit,
}: TaskDetailSheetProps) {
  const perms = usePermissions();
  const { project } = useCurrentProject();
  const pipelineStatuses = getEffectivePipelineStatuses(project);
  const currentUser = useAuthStore((s) => s.user);

  // ─── Attachments wiring ────────────────────────────────────────────────
  // The hook is dormant when the sheet is closed or no task is loaded
  // (entityId: null). Once open with a task, it owns the live list of
  // attachments — drives the section header count AND fires the
  // optimistic-update callback to the parent so the table badge / kanban
  // chip / etc. stay in sync without a list refetch.
  const attachments = useAttachments({
    slug: projectSlug,
    entityType: "task",
    entityId: open && task ? task.id : null,
  });

  /** Per-row delete gate inside the AttachmentZone — mirrors the
   *  Company-side rule (admin OR uploader-is-me). canEditTasks
   *  covers admins; the uploader-match path covers a regular
   *  member's own files. */
  const canRemoveAttachment = useCallback(
    (att: AttachmentRead) => {
      if (perms.canEditTasks) return true;
      if (!currentUser || !att.uploader_user_id) return false;
      return att.uploader_user_id === currentUser.id;
    },
    [perms.canEditTasks, currentUser],
  );

  // Local data — fetched lazily on open.
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [companyDetail, setCompanyDetail] = useState<CompanyDetail | null>(null);
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);
  const companyIdRef = useRef<number | null>(null);
  companyIdRef.current = open ? (task?.company_id ?? null) : null;

  // The company's status and notes are worked from the task itself
  // (Romaric, Prism task #270, 2026-10-26: "changer le statut de
  // l'entreprise directement dans la task … prendre des notes dans la task
  // et que ça crée une activité dans l'entreprise").
  const [statusBusy, setStatusBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  // Inline-edit popover open states.
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const [duePickerOpen, setDuePickerOpen] = useState(false);
  const [priorityPickerOpen, setPriorityPickerOpen] = useState(false);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);

  // Description local state — debounced save on blur.
  const [descEditing, setDescEditing] = useState(false);
  const [descDraft, setDescDraft] = useState("");

  // Confirmation + busy state.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);

  // Reset description draft + close popovers whenever the task changes.
  useEffect(() => {
    setDescDraft(task?.description ?? "");
    setDescEditing(false);
    setAssigneePickerOpen(false);
    setDuePickerOpen(false);
    setPriorityPickerOpen(false);
    setCompanyPickerOpen(false);
  }, [task?.id, task?.description]);

  // A note in progress belongs to its task.
  useEffect(() => {
    setNoteDraft("");
  }, [task?.id]);

  // Propagate the live attachments_count to the parent list whenever
  // the hook's count diverges from the task object. This is what makes
  // the table badge / kanban chip / dashboard list etc. update
  // optimistically after a sheet upload or delete — every parent
  // surface that owns a task list already wires `onTaskUpdated` to
  // patch its local state, so we just emit the patched task here.
  // Also invalidates the popover cache so the next badge click
  // refetches the canonical list rather than serving stale rows.
  useEffect(() => {
    if (!task) return;
    if (!attachments.loaded) return;
    const liveCount = attachments.attachments.length;
    if (task.attachments_count === liveCount) return;
    onTaskUpdated({ ...task, attachments_count: liveCount });
    invalidateAttachmentsCellCache(task.id);
    // onTaskUpdated changes on every parent render — including it
    // would re-fire on every keystroke into the parent. The task.id
    // + count pair is sufficient to detect the meaningful change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task?.id,
    task?.attachments_count,
    attachments.attachments.length,
    attachments.loaded,
  ]);

  // Fetch members on open if needed.
  useEffect(() => {
    if (!open) return;
    if (members.length > 0) return;
    let cancelled = false;
    listMembers(projectSlug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [open, projectSlug, members.length]);

  // Fetch the full company detail when the task has a linked company.
  useEffect(() => {
    if (!open || !task?.company_id) {
      setCompanyDetail(null);
      return;
    }
    let cancelled = false;
    setCompanyDetailLoading(true);
    getCompany(projectSlug, task.company_id)
      .then((c) => !cancelled && setCompanyDetail(c))
      .catch(() => !cancelled && setCompanyDetail(null))
      .finally(() => !cancelled && setCompanyDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, projectSlug, task?.company_id]);

  // Re-read the company after a change made from here: a new status or a
  // note is a new activity, which the Activity section then shows.
  const refreshCompany = useCallback(async () => {
    const companyId = companyIdRef.current;
    if (companyId === null) return;
    try {
      const fresh = await getCompany(projectSlug, companyId);
      if (companyIdRef.current === companyId) setCompanyDetail(fresh);
    } catch {
      // The change itself was saved; the next opening shows it.
    }
  }, [projectSlug]);

  // ─── Mutations ────────────────────────────────────────────────────────────

  const patch = useCallback(
    async (payload: TaskUpdate) => {
      if (!task) return;
      const previous = task;
      // Optimistic — apply to caller's cache immediately.
      const optimistic: TaskRead = {
        ...task,
        ...payloadToTask(payload, task),
      };
      onTaskUpdated(optimistic);
      try {
        const updated = await updateTask(projectSlug, task.id, payload);
        onTaskUpdated(updated);
      } catch (err) {
        onTaskUpdated(previous);
        toast.error(extractMessage(err));
      }
    },
    [task, projectSlug, onTaskUpdated],
  );

  async function handleToggleComplete() {
    if (!task || completeBusy) return;
    setCompleteBusy(true);
    try {
      const updated = task.is_completed
        ? await reopenTask(projectSlug, task.id)
        : await completeTask(projectSlug, task.id);
      onTaskUpdated(updated);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setCompleteBusy(false);
    }
  }

  async function handleDelete() {
    if (!task) return;
    setDeleting(true);
    try {
      await deleteTask(projectSlug, task.id);
      onTaskDeleted(task.id);
      toast.success("Task deleted");
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  async function changeCompanyStatus(next: string) {
    const company = companyDetail;
    if (!company || statusBusy || next === company.pipeline_status) return;
    setStatusBusy(true);
    setCompanyDetail({ ...company, pipeline_status: next });
    try {
      await updateCompany(projectSlug, company.id, { pipeline_status: next });
      toast.success(
        `${company.name}: status set to ${statusLabel(pipelineStatuses, next)}`,
      );
      await refreshCompany();
    } catch (err) {
      setCompanyDetail((current) =>
        current?.id === company.id
          ? { ...current, pipeline_status: company.pipeline_status }
          : current,
      );
      toast.error(extractMessage(err, "Couldn't change the company status."));
    } finally {
      setStatusBusy(false);
    }
  }

  async function addNote() {
    const text = noteDraft.trim();
    if (!task?.company_id || !text || noteSaving) return;
    setNoteSaving(true);
    try {
      // The company's own timeline keeps the note, with the task it came from.
      await logAction(projectSlug, task.company_id, {
        action: "note_added",
        details: { text, task_id: task.id, task_title: task.title },
      });
      setNoteDraft("");
      toast.success("Note added to the company's activity");
      await refreshCompany();
    } catch (err) {
      toast.error(extractMessage(err, "Couldn't add the note."));
    } finally {
      setNoteSaving(false);
    }
  }

  function commitDescription() {
    if (!task) return;
    setDescEditing(false);
    const next = descDraft.trim() === "" ? null : descDraft;
    if ((task.description ?? null) === next) return;
    void patch({ description: next });
  }

  // ─── Derived ──────────────────────────────────────────────────────────────

  const completed = !!task?.is_completed;
  const dueLocal = useMemo(() => splitDateTime(task?.due_at ?? null), [task?.due_at]);
  const slots = useMemo(() => timeSlots(), []);
  const canEdit = perms.canEditTasks;
  const canDelete = perms.canDeleteTasks;

  const assignedMember = useMemo(
    () => members.find((m) => m.id === task?.assigned_to_id) ?? null,
    [members, task?.assigned_to_id],
  );

  const recentActivities = useMemo(() => {
    if (!companyDetail) return [];
    return companyDetail.activities.slice(0, ACTIVITY_LIMIT);
  }, [companyDetail]);

  // Every link to the task's company lets the company page lead back here.
  // From that company's own page (its Tasks card), the link only reveals it.
  const location = useLocation();
  const companyPath = task?.company_id
    ? `/projects/${projectSlug}/companies/${task.company_id}`
    : null;
  const companyLink: CompanyLinkProps | null =
    task && companyPath
      ? {
          to: companyPath,
          state: fromTaskState(task),
          onClick: (event) => {
            if (location.pathname !== companyPath) return;
            event.preventDefault();
            onOpenChange(false);
          },
        }
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[460px]">
        <SheetTitle className="sr-only">
          {task?.title ?? "Task detail"}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Task detail panel with inline editing.
        </SheetDescription>

        {!task ? null : (
          <>
            {/* ─── Header ─────────────────────────────────────────────────── */}
            <div className="flex items-start gap-3 border-b border-slate-900/[0.06] px-6 pb-5 pt-6 pr-12">
              <button
                type="button"
                onClick={handleToggleComplete}
                disabled={!canEdit || completeBusy}
                aria-label={completed ? "Reopen task" : "Complete task"}
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  completed
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-slate-300 bg-white text-transparent hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-500",
                  !canEdit &&
                    "cursor-default opacity-60 hover:border-slate-300 hover:bg-white hover:text-transparent",
                )}
              >
                {completeBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-5 w-5" strokeWidth={3} />
                )}
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3">
                  <h2
                    className={cn(
                      "min-w-0 flex-1 break-words text-xl font-semibold tracking-tight",
                      completed
                        ? "text-slate-400 line-through"
                        : "text-slate-900",
                    )}
                  >
                    {task.title}
                  </h2>
                  <span
                    className={cn(
                      "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                      PRIORITY_RING[task.priority],
                    )}
                  >
                    {PRIORITY_LABEL[task.priority]}
                  </span>
                </div>
                {completed && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                      <Check className="h-3 w-3" />
                      Completed
                    </span>
                    {(task.completed_by_name || task.completed_at) && (
                      <span className="text-slate-400">
                        {task.completed_by_name
                          ? `by ${task.completed_by_name}`
                          : ""}
                        {task.completed_at &&
                          ` · ${formatRelativeDate(task.completed_at)}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ─── Body ────────────────────────────────────────────────────── */}
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {/* Details card */}
              <div className="space-y-4 rounded-xl bg-slate-50 p-5">
                {/* Assigned to */}
                <Row icon={User} label="Assigned to">
                  {canEdit ? (
                    <Popover
                      open={assigneePickerOpen}
                      onOpenChange={setAssigneePickerOpen}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 text-sm transition-colors hover:bg-slate-100"
                        >
                          <Avatar className="h-8 w-8 border border-slate-200">
                            <AvatarFallback className="bg-slate-900 text-[11px] font-semibold text-white">
                              {initials(task.assigned_to_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 truncate font-medium text-slate-900">
                            {task.assigned_to_name}
                          </span>
                          {assignedMember && (
                            <span className="shrink-0 rounded-full border border-slate-200 bg-white px-1.5 py-0 text-[10px] font-medium text-slate-500">
                              {assignedMember.role.name}
                            </span>
                          )}
                          <ChevronsUpDown className="ml-1 h-3 w-3 text-slate-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-[280px] p-0"
                      >
                        <Command>
                          <CommandInput placeholder="Search members…" />
                          <CommandList>
                            <CommandEmpty>No member found.</CommandEmpty>
                            <CommandGroup>
                              {members.map((m) => (
                                <CommandItem
                                  key={m.id}
                                  value={`${m.full_name} ${m.email}`}
                                  onSelect={() => {
                                    setAssigneePickerOpen(false);
                                    if (m.id !== task.assigned_to_id) {
                                      void patch({ assigned_to_id: m.id });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-3.5 w-3.5",
                                      m.id === task.assigned_to_id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <Avatar className="mr-2 h-5 w-5 border border-slate-200">
                                    <AvatarFallback className="bg-slate-900 text-[9px] font-semibold text-white">
                                      {initials(m.full_name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="min-w-0 truncate">
                                    {m.full_name}
                                  </span>
                                  <span className="ml-2 text-xs text-slate-400">
                                    {m.role.name}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <Avatar className="h-8 w-8 border border-slate-200">
                        <AvatarFallback className="bg-slate-900 text-[11px] font-semibold text-white">
                          {initials(task.assigned_to_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-slate-900">
                        {task.assigned_to_name}
                      </span>
                    </div>
                  )}
                </Row>

                {/* Due date */}
                <Row icon={Calendar} label="Due date">
                  <DueValue
                    task={task}
                    canEdit={canEdit}
                    open={duePickerOpen}
                    onOpenChange={setDuePickerOpen}
                    initialDate={dueLocal.date}
                    initialTime={dueLocal.time}
                    slots={slots}
                    onSave={(date, time) =>
                      void patch({
                        due_at: combineDateTime(date, time),
                      })
                    }
                    onClear={() => void patch({ due_at: null })}
                  />
                </Row>

                {/* Priority */}
                <Row icon={Flag} label="Priority">
                  {canEdit ? (
                    <Popover
                      open={priorityPickerOpen}
                      onOpenChange={setPriorityPickerOpen}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-2 rounded-md px-1 py-0.5 text-sm transition-colors hover:bg-slate-100"
                        >
                          <span
                            className={cn(
                              "h-2.5 w-2.5 rounded-full",
                              PRIORITY_DOT[task.priority],
                            )}
                          />
                          <span className="font-medium text-slate-900">
                            {PRIORITY_LABEL[task.priority]}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 text-slate-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-44 p-2">
                        <div className="space-y-1">
                          {PRIORITIES.slice()
                            .reverse()
                            .map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => {
                                  setPriorityPickerOpen(false);
                                  if (p !== task.priority)
                                    void patch({ priority: p });
                                }}
                                className={cn(
                                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50",
                                  p === task.priority && "bg-slate-50",
                                )}
                              >
                                <span
                                  className={cn(
                                    "h-2.5 w-2.5 rounded-full",
                                    PRIORITY_DOT[p],
                                  )}
                                />
                                <span className="text-slate-900">
                                  {PRIORITY_LABEL[p]}
                                </span>
                              </button>
                            ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={cn(
                          "h-2.5 w-2.5 rounded-full",
                          PRIORITY_DOT[task.priority],
                        )}
                      />
                      <span className="font-medium text-slate-900">
                        {PRIORITY_LABEL[task.priority]}
                      </span>
                    </div>
                  )}
                </Row>

                {/* Created by */}
                {task.created_by_name && (
                  <Row icon={User} label="Created by">
                    <div className="flex items-center gap-2 text-sm">
                      <Avatar className="h-5 w-5 border border-slate-200">
                        <AvatarFallback className="bg-slate-300 text-[9px] font-semibold text-white">
                          {initials(task.created_by_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-slate-700">
                        {task.created_by_name}
                      </span>
                      <span className="text-xs text-slate-400">
                        · created {formatRelativeDate(task.created_at)}
                      </span>
                    </div>
                  </Row>
                )}

                {/* Company. "Unlink" sits under the label, out of the way of
                 *  the status and the director (Romaric, Prism task #272). */}
                <Row
                  icon={Building2}
                  label="Company"
                  action={
                    canEdit && task.company_id ? (
                      <button
                        type="button"
                        onClick={() => void patch({ company_id: null })}
                        className="text-xs text-slate-400 hover:text-slate-600"
                      >
                        Unlink
                      </button>
                    ) : undefined
                  }
                >
                  <CompanyValue
                    task={task}
                    projectSlug={projectSlug}
                    canEdit={canEdit}
                    companyDetail={companyDetail}
                    companyDetailLoading={companyDetailLoading}
                    open={companyPickerOpen}
                    onOpenChange={setCompanyPickerOpen}
                    pipelineStatuses={pipelineStatuses}
                    link={companyLink}
                    canChangeStatus={perms.canEdit}
                    statusBusy={statusBusy}
                    onStatusChange={(next) => void changeCompanyStatus(next)}
                    onPick={(companyId) => {
                      setCompanyPickerOpen(false);
                      if (companyId !== task.company_id) {
                        void patch({ company_id: companyId });
                      }
                    }}
                  />
                </Row>
              </div>

              {/* Description */}
              <section>
                <SectionHeader>Description</SectionHeader>
                {descEditing ? (
                  <Textarea
                    value={descDraft}
                    autoFocus
                    onChange={(e) => setDescDraft(e.target.value)}
                    onBlur={commitDescription}
                    rows={4}
                    placeholder="Add context, links, next steps…"
                    className="rounded-lg border-slate-200"
                  />
                ) : (
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => {
                      if (canEdit) {
                        setDescDraft(task.description ?? "");
                        setDescEditing(true);
                      }
                    }}
                    className={cn(
                      "group/desc flex w-full items-start justify-between gap-2 rounded-lg px-2 py-1 text-left text-sm",
                      canEdit && "hover:bg-slate-50",
                    )}
                  >
                    {task.description ? (
                      <span className="whitespace-pre-wrap text-slate-700">
                        {task.description}
                      </span>
                    ) : (
                      <span className="italic text-slate-400">
                        No description
                      </span>
                    )}
                    {canEdit && (
                      <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 opacity-0 transition-opacity group-hover/desc:opacity-100" />
                    )}
                  </button>
                )}
              </section>

              {/* Scheduling — only for calls / meetings. Surfaces the
               *  start time, duration, location and contact, plus
               *  one-click links to Google / Outlook / a downloadable
               *  ICS so the user can drop the event onto their own
               *  calendar without leaving the sheet. */}
              {(task.task_type === "call" ||
                task.task_type === "meeting") && (
                <SchedulingSection
                  task={task}
                  projectSlug={projectSlug}
                  canEdit={canEdit}
                  companyLink={companyLink}
                  onUpdated={onTaskUpdated}
                />
              )}

              {/* Activity (only when company linked): the company's
               *  latest entries, and notes written here land among them. */}
              {task.company_id && (
                <section>
                  <SectionHeader>Activity</SectionHeader>
                  {perms.canLogNote && (
                    <NoteComposer
                      value={noteDraft}
                      onChange={setNoteDraft}
                      onSubmit={() => void addNote()}
                      saving={noteSaving}
                      companyName={companyDetail?.name ?? task.company_name}
                    />
                  )}
                  {companyDetailLoading ? (
                    <div className="flex items-center py-2 text-xs text-slate-400">
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Loading…
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      No recent activity.
                    </p>
                  ) : (
                    <>
                      <ActivityTimeline
                        items={recentActivities}
                        members={members}
                        pipelineStatuses={pipelineStatuses}
                        contacts={companyDetail?.contacts ?? []}
                      />
                      {companyLink && (
                        <Link
                          {...companyLink}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          View full timeline
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </>
                  )}
                </section>
              )}

              {/* Attachments — fully functional even though the sheet is
               *  otherwise read-only-ish. The hook owns the live count
               *  for the section header; AttachmentZone owns the drop /
               *  upload / delete / download UX (Phase A + C). */}
              <section>
                <div className="mb-2 flex items-center gap-2">
                  <SectionHeader>Attachments</SectionHeader>
                  {attachments.loaded && (
                    <span className="text-[11px] tabular-nums text-slate-400">
                      {attachments.attachments.length}
                    </span>
                  )}
                </div>
                <AttachmentZone
                  mode="live"
                  variant="expanded"
                  attachments={attachments.attachments}
                  isLoading={attachments.isLoading && !attachments.loaded}
                  cap={attachments.cap}
                  canUpload={perms.canEditTasks}
                  canRemove={canRemoveAttachment}
                  onUpload={attachments.upload}
                  onRemove={attachments.remove}
                  onDownload={attachments.download}
                  imageThumbnails={{
                    slug: projectSlug,
                    entityType: "task",
                    entityId: task.id,
                  }}
                />
              </section>
            </div>

            {/* ─── Footer ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-end gap-2 border-t border-slate-900/[0.06] px-6 py-4">
              {canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
              {canEdit && onAskEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAskEdit(task)}
                >
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  Edit
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>

            {/* Delete confirm */}
            <Dialog
              open={confirmDelete}
              onOpenChange={(o) => {
                if (deleting) return;
                if (!o) setConfirmDelete(false);
              }}
            >
              <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                  <DialogTitle>Delete this task?</DialogTitle>
                  <DialogDescription>
                    This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  <p className="text-xs leading-relaxed text-rose-700">
                    The task &ldquo;{task.title}&rdquo; will be permanently
                    deleted.
                  </p>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                  >
                    {deleting && (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    )}
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Row({
  icon: Icon,
  label,
  action,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Under the label, aligned with its text. */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-3">
      <div className="space-y-1 pt-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        {action && <div className="pl-[18px]">{action}</div>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
      {children}
    </h3>
  );
}

interface DueValueProps {
  task: TaskRead;
  canEdit: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDate: string;
  initialTime: string;
  slots: string[];
  onSave: (date: string | null, time: string | null) => void;
  onClear: () => void;
}

function DueValue({
  task,
  canEdit,
  open,
  onOpenChange,
  initialDate,
  initialTime,
  slots,
  onSave,
  onClear,
}: DueValueProps) {
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);

  // Reset draft whenever the popover (re-)opens.
  useEffect(() => {
    if (open) {
      setDate(initialDate);
      setTime(initialTime);
    }
  }, [open, initialDate, initialTime]);

  const display = useMemo(() => {
    if (!task.due_at) {
      return <span className="text-sm italic text-slate-400">No due date</span>;
    }
    const d = new Date(task.due_at);
    const formattedDate = d.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const hadTime = initialTime !== "";
    const formattedTime = hadTime
      ? d.toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
    return (
      <span className="text-sm font-medium text-slate-900">
        {formattedDate}
        {formattedTime && ` at ${formattedTime}`}
      </span>
    );
  }, [task.due_at, initialTime]);

  const badge = useMemo(() => {
    if (!task.due_at) return null;
    if (task.is_overdue) {
      return (
        <span className="ml-2 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
          Overdue
        </span>
      );
    }
    const d = new Date(task.due_at);
    const now = new Date();
    if (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    ) {
      return (
        <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
          Today
        </span>
      );
    }
    return null;
  }, [task.due_at, task.is_overdue]);

  if (!canEdit) {
    return (
      <div className="flex items-center">
        {display}
        {badge}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center rounded-md px-1 py-0.5 text-left transition-colors hover:bg-slate-100"
        >
          {display}
          {badge}
          <ChevronsUpDown className="ml-auto h-3 w-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-3">
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Date
            </label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 rounded-lg border-slate-200"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Time
            </label>
            <Select
              value={time === "" ? NO_TIME : time}
              onValueChange={(v) => setTime(v === NO_TIME ? "" : v)}
            >
              <SelectTrigger className="h-9 rounded-lg border-slate-200 bg-white">
                <SelectValue placeholder="No time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TIME}>No time</SelectItem>
                {slots.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onClear();
                onOpenChange(false);
              }}
              className="text-slate-500"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                onSave(date || null, time || null);
                onOpenChange(false);
              }}
              disabled={!date}
            >
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Props of every link from the drawer to the task's company. */
interface CompanyLinkProps {
  to: string;
  state: Record<string, unknown>;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface CompanyValueProps {
  task: TaskRead;
  projectSlug: string;
  canEdit: boolean;
  companyDetail: CompanyDetail | null;
  companyDetailLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipelineStatuses: PipelineStatus[];
  onPick: (companyId: number | null) => void;
  link: CompanyLinkProps | null;
  canChangeStatus: boolean;
  statusBusy: boolean;
  onStatusChange: (statusKey: string) => void;
}

function CompanyValue({
  task,
  projectSlug,
  canEdit,
  companyDetail,
  companyDetailLoading,
  open,
  onOpenChange,
  pipelineStatuses,
  onPick,
  link,
  canChangeStatus,
  statusBusy,
  onStatusChange,
}: CompanyValueProps) {
  // Server-side company search — the project may hold 20k+ companies,
  // so we query the backend as the user types (min 2 chars, debounced)
  // rather than preloading the list.
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [results, setResults] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listCompanies(projectSlug, { q, page_size: 20 })
      .then((res) => !cancelled && setResults(res.items))
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery, projectSlug]);

  if (task.company_id) {
    const c = companyDetail;
    return (
      <div className="space-y-2">
        <Link
          to={link?.to ?? `/projects/${projectSlug}/companies/${task.company_id}`}
          state={link?.state}
          onClick={link?.onClick}
          className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-bold text-slate-600">
            {(c?.name ?? task.company_name ?? "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-900">
              {c?.name ?? task.company_name}
              {companyDetailLoading && !c && (
                <Loader2 className="ml-2 inline h-3 w-3 animate-spin text-slate-400" />
              )}
            </div>
            {c?.domain && (
              <div className="truncate text-xs text-slate-400">
                {c.domain}
              </div>
            )}
          </div>
          {c?.effective_score != null && (
            <span
              className={cn(
                "shrink-0 rounded-md bg-slate-50 px-1.5 py-0.5 text-xs font-semibold",
                scoreColor(c.effective_score),
              )}
            >
              {c.effective_score}
            </span>
          )}
        </Link>
        {c && (
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 gap-y-1.5 text-xs">
            {/* The company's status, changed here without leaving the task. */}
            <span className="pt-0.5 text-slate-400">Status</span>
            <div>
              {canChangeStatus ? (
                <CompanyStatusMenu
                  statusKey={c.pipeline_status}
                  statuses={pipelineStatuses}
                  busy={statusBusy}
                  onChange={onStatusChange}
                />
              ) : (
                <PipelineStatusBadge
                  statusKey={c.pipeline_status}
                  statuses={pipelineStatuses}
                />
              )}
            </div>
            <DirectorLine contact={taskDirector(task, c)} />
          </div>
        )}
      </div>
    );
  }

  // No company linked.
  if (!canEdit) {
    return (
      <span className="text-sm italic text-slate-400">No company linked</span>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 justify-start border-dashed text-slate-500"
        >
          <Plus className="mr-2 h-3.5 w-3.5" />
          Link a company
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search companies…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {query.trim().length < 2 ? (
              <div className="py-6 text-center text-sm text-slate-400">
                Type at least 2 characters to search
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : (
              <>
                <CommandEmpty>No company found.</CommandEmpty>
                <CommandGroup>
                  {results.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`${c.name} ${c.domain ?? ""}`}
                      onSelect={() => onPick(c.id)}
                    >
                      <span className="min-w-0 truncate">{c.name}</span>
                      {c.domain && (
                        <span className="ml-2 truncate text-xs text-slate-400">
                          {c.domain}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Who the task's call is with: the task's own contact, else the company's
 *  first decision maker, else its first contact — the backend's primary
 *  contact. */
function taskDirector(
  task: TaskRead,
  company: CompanyDetail,
): EmbeddedContact | null {
  const contacts = company.contacts ?? [];
  return (
    contacts.find((c) => c.id === task.contact_id) ??
    contacts.find((c) => c.is_decision_maker) ??
    contacts[0] ??
    null
  );
}

/** The director's name and number under the status (Romaric, Prism task
 *  #273: "Mettre numéro + nom du dirigeant"). A number the reader may not
 *  see says so, as everywhere else. */
function DirectorLine({ contact }: { contact: EmbeddedContact | null }) {
  if (!contact) return null;
  const href = phoneHref(contact.phone);
  return (
    <>
      <span className="text-slate-400">
        {contact.is_decision_maker ? "Director" : "Contact"}
      </span>
      <div className="min-w-0">
        <div className="truncate font-medium text-slate-700">
          {contact.full_name}
          {contact.title && (
            <span className="font-normal text-slate-400"> · {contact.title}</span>
          )}
        </div>
        {href ? (
          <span className="inline-flex items-center gap-0.5">
            <a
              href={href}
              className="inline-flex items-center gap-1 tabular-nums text-indigo-600 hover:text-indigo-500"
            >
              <Phone className="h-3 w-3" />
              {formatPhone(contact.phone)}
            </a>
            <CopyButton
              value={phoneForCopy(contact.phone ?? "")}
              label="phone number"
            />
            {phoneCopiedFromCompany(contact) && (
              <span className="ml-1 text-[11px] italic text-slate-400">
                ({COPIED_FROM_COMPANY_LABEL})
              </span>
            )}
          </span>
        ) : isHidden(contact, "phone") ? (
          <HiddenDetail label="Phone" className="text-xs" />
        ) : (
          <span className="text-slate-400">No phone</span>
        )}
      </div>
    </>
  );
}

function statusLabel(statuses: PipelineStatus[], key: string): string {
  return statuses.find((s) => s.key === key)?.label ?? key.replace(/_/g, " ");
}

/** The company's status, changed from the task: the same choices as the
 *  status menu of the company page. */
function CompanyStatusMenu({
  statusKey,
  statuses,
  busy,
  onChange,
}: {
  statusKey: string;
  statuses: PipelineStatus[];
  busy: boolean;
  onChange: (statusKey: string) => void;
}) {
  const colors = getStatusColor(statusKey, statuses);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={busy}>
        <button
          type="button"
          aria-label="Change the company status"
          className={cn(
            "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 disabled:cursor-wait disabled:opacity-60",
            colors.bg,
            colors.text,
            colors.border,
          )}
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <span className={cn("h-1.5 w-1.5 rounded-full", colors.dot)} />
          )}
          <span className="capitalize">{statusLabel(statuses, statusKey)}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Set company status to</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {statuses
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((s) => (
            <DropdownMenuItem key={s.key} onSelect={() => onChange(s.key)}>
              <span
                className={cn(
                  "mr-2 h-1.5 w-1.5 rounded-full",
                  getStatusColor(s.key, statuses).dot,
                )}
              />
              {s.label}
              {s.key === statusKey && (
                <Check className="ml-auto h-3.5 w-3.5 text-slate-500" />
              )}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A note written in the task, saved to the company's activity. */
function NoteComposer({
  value,
  onChange,
  onSubmit,
  saving,
  companyName,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  saving: boolean;
  companyName: string | null;
}) {
  const empty = value.trim() === "";
  return (
    <div className="mb-3 space-y-2">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
          }
        }}
        rows={3}
        // Read-only, not disabled, while saving: the caret stays here for
        // the next note.
        readOnly={saving}
        placeholder={`Take a note: it goes to ${companyName ?? "the company"}'s activity`}
        aria-label="Note for the company's activity"
        className="rounded-lg border-slate-200 text-sm"
      />
      <div className="flex items-center justify-end gap-2">
        {!empty && (
          <span className="text-[11px] text-slate-400">
            {/Mac|iPod|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl"}
            +Enter
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={empty || saving}
          onClick={onSubmit}
        >
          {saving ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <StickyNote className="mr-2 h-3.5 w-3.5" />
          )}
          Add note
        </Button>
      </div>
    </div>
  );
}

function PipelineStatusBadge({
  statusKey,
  statuses,
}: {
  statusKey: string;
  statuses: PipelineStatus[];
}) {
  const colors = getStatusColor(statusKey, statuses);
  const label = statusLabel(statuses, statusKey);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-medium",
        colors.bg,
        colors.text,
        colors.border,
      )}
    >
      <span className={cn("h-1 w-1 rounded-full", colors.dot)} />
      <span className="capitalize">{label}</span>
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Best-effort optimistic merge — applies the patched fields onto the cached
 * task. Server returns the canonical row right after, so any drift is fixed
 * within one tick.
 */
function payloadToTask(payload: TaskUpdate, current: TaskRead): Partial<TaskRead> {
  const out: Partial<TaskRead> = {};
  if (payload.title !== undefined && payload.title !== null)
    out.title = payload.title;
  if (payload.description !== undefined)
    out.description = payload.description;
  if (payload.company_id !== undefined) {
    out.company_id = payload.company_id;
    if (payload.company_id === null) out.company_name = null;
  }
  if (payload.assigned_to_id !== undefined && payload.assigned_to_id !== null) {
    out.assigned_to_id = payload.assigned_to_id;
  }
  if (payload.due_at !== undefined) out.due_at = payload.due_at;
  if (payload.priority !== undefined && payload.priority !== null)
    out.priority = payload.priority;
  if (
    payload.is_completed !== undefined &&
    payload.is_completed !== null
  )
    out.is_completed = payload.is_completed;
  // Reuse current values for fields we didn't touch.
  return { ...current, ...out };
}

function extractMessage(
  err: unknown,
  fallback = "Couldn't update the task.",
): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return fallback;
}

interface SchedulingSectionProps {
  task: TaskRead;
  projectSlug: string;
  canEdit: boolean;
  companyLink: CompanyLinkProps | null;
  onUpdated: (task: TaskRead) => void;
}

const SCHEDULE_DURATION_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1h 30" },
  { value: 120, label: "2 hours" },
];

function SchedulingSection({
  task,
  projectSlug,
  canEdit,
  companyLink,
  onUpdated,
}: SchedulingSectionProps) {
  const startIso = task.scheduled_at ?? task.due_at;
  const durationMin = task.duration_minutes ?? 30;
  const startDate = startIso ? new Date(startIso) : null;
  const isMeeting = task.task_type === "meeting";
  const TypeIcon = isMeeting ? Handshake : Phone;
  const accent = isMeeting
    ? "border-violet-300 bg-violet-50 text-violet-700"
    : "border-blue-300 bg-blue-50 text-blue-700";

  // Read-mode display values. The primary line shows the time in the
  // task's saved timezone (creator's intent); when the viewer is in
  // another zone we also surface their local wall-clock in muted text.
  const taskTz =
    task.scheduled_timezone && task.scheduled_timezone.length > 0
      ? task.scheduled_timezone
      : null;
  const viewerTz = getDefaultTimezone();
  const effectiveTz = taskTz ?? viewerTz;
  const showViewerLocal = !!startDate && !!taskTz && taskTz !== viewerTz;
  const dateTimeLabel =
    startDate && formatDateTimeInTz(startDate, effectiveTz);
  const viewerLocalLabel =
    showViewerLocal && startDate
      ? formatTimeInTz(startDate, viewerTz)
      : null;
  const durationLabel = formatDurationMin(durationMin);
  const locationIsLink =
    !!task.location && /^https?:\/\//i.test(task.location.trim());

  const gUrl = googleCalendarUrl(task);
  const oUrl = outlookCalendarUrl(task);

  // Edit mode — when the user clicks "Edit", we swap the dl block
  // for inputs. The wall-clock the user sees in the inputs is the
  // task's saved zone, NOT the viewer's: changing the timezone keeps
  // the displayed date+time and just re-resolves the UTC instant.
  const slots = useMemo(() => timeSlots(), []);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editDate, setEditDate] = useState<string>("");
  const [editTime, setEditTime] = useState<string>("");
  const [editTz, setEditTz] = useState<string>(effectiveTz);
  const [editDuration, setEditDuration] = useState<number>(durationMin);

  function startEdit() {
    if (!canEdit) return;
    const split = startIso
      ? splitDateTimeInTz(startIso, effectiveTz)
      : { date: "", time: "" };
    setEditDate(split.date);
    setEditTime(split.time || "09:00");
    setEditTz(effectiveTz);
    setEditDuration(durationMin);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  async function saveEdit() {
    if (!editDate || !editTime) {
      toast.error("Pick a date and time.");
      return;
    }
    setSaving(true);
    try {
      // Naive ISO + timezone — the backend resolves the UTC instant.
      // Sending the wall-clock the user sees keeps the form's
      // "I want this at 14:00 in Paris" intent intact regardless of
      // the client's own zone.
      const naive = toNaiveIsoDateTime(editDate, editTime);
      const payload: TaskUpdate = {
        scheduled_at: naive,
        due_at: naive,
        scheduled_timezone: editTz,
        duration_minutes: editDuration,
      };
      const updated = await updateTask(projectSlug, task.id, payload);
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <SectionHeader>Scheduling</SectionHeader>
      <div className="space-y-3 rounded-xl bg-white p-3 shadow-card-rest">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium",
              accent,
            )}
          >
            <TypeIcon className="h-3 w-3" />
            {isMeeting ? "Meeting" : "Call"}
          </span>
          {task.contact_name &&
            (companyLink ? (
              <Link
                {...companyLink}
                className="truncate text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                {task.contact_name}
              </Link>
            ) : (
              <span className="truncate text-sm font-medium text-slate-700">
                {task.contact_name}
              </span>
            ))}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={startEdit}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              aria-label="Edit scheduling"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  Date
                </label>
                <Input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="h-9 rounded-lg border-slate-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  Time
                </label>
                <Select
                  value={editTime}
                  onValueChange={(v) => setEditTime(v)}
                >
                  <SelectTrigger
                    className="h-9 rounded-lg border-slate-200 bg-white"
                    aria-label="Time"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Timezone
              </label>
              <TimezoneSelect
                value={editTz}
                onValueChange={setEditTz}
                className="h-9"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Duration
              </label>
              <Select
                value={String(editDuration)}
                onValueChange={(v) => setEditDuration(Number(v))}
              >
                <SelectTrigger
                  className="h-9 rounded-lg border-slate-200 bg-white"
                  aria-label="Duration"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_DURATION_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
                className="h-7 px-2 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void saveEdit()}
                disabled={saving}
                className="h-7 px-3 text-xs"
              >
                {saving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <dl className="space-y-1.5 text-sm">
            {dateTimeLabel && (
              <div className="flex items-start gap-2 text-slate-700">
                <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <div>{dateTimeLabel}</div>
                  {viewerLocalLabel && (
                    <div className="text-[11px] text-slate-400">
                      {viewerLocalLabel} for you
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-slate-700">
              <Hourglass className="h-3.5 w-3.5 text-slate-400" />
              <span>{durationLabel}</span>
            </div>
            {taskTz && (
              <div className="flex items-center gap-2 text-slate-500">
                <Globe className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate">{formatTimezoneLabel(taskTz)}</span>
              </div>
            )}
            {task.location && (
              <div className="flex items-center gap-2 text-slate-700">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                {locationIsLink ? (
                  <a
                    href={task.location}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 truncate text-indigo-600 hover:text-indigo-500"
                  >
                    <span className="truncate">{task.location}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  <span className="truncate">{task.location}</span>
                )}
              </div>
            )}
            {task.contact_email && (
              <div className="flex items-center gap-2 text-slate-500">
                <User className="h-3.5 w-3.5 text-slate-400" />
                <span className="truncate">{task.contact_email}</span>
                <CopyButton value={task.contact_email} label="email" />
              </div>
            )}
          </dl>
        )}

        {!editing && (gUrl || oUrl || startDate) && (
          <div className="flex flex-wrap gap-2 border-t border-slate-900/[0.06] pt-2">
            {gUrl && (
              <a
                href={gUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
              >
                <Calendar className="h-3.5 w-3.5" />
                Add to Google Calendar
              </a>
            )}
            {oUrl && (
              <a
                href={oUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
              >
                <Calendar className="h-3.5 w-3.5" />
                Add to Outlook
              </a>
            )}
            <button
              type="button"
              onClick={() => downloadIcs(task)}
              disabled={!startDate}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download .ics
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function formatDurationMin(min: number): string {
  if (min < 60) return `${min} minutes`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return h === 1 ? "1 hour" : `${h} hours`;
  return `${h}h ${m}m`;
}
